<template>
  <div>
    <div class="field">
      <label class="label">{{ $tc('country', 1) }}</label>
      <div class="control">
        <div class="select is-fullwidth">
          <select v-model="countryCode"
                  :class="{'is-danger': !isFormValid}">
            <option disabled>
              {{ $t('selectAField', {field: $t('country')}) }}
            </option>
            <option v-for="country in countryCodes"
                    :key="country.key"
                    :value="country.key">
              {{ country.name }}
            </option>
          </select>
        </div>
      </div>
    </div>
    <div class="field">
      <label class="label">{{ $t('line') }}</label>
      <div class="control mb-1">
        <input v-model="line1"
               class="input"
               type="text"
               maxlength="255">
      </div>
      <div class="control mb-1">
        <input v-model="line2"
               class="input"
               type="text"
               maxlength="255">
      </div>
      <div class="control mb-1">
        <input v-model="line3"
               class="input"
               type="text"
               maxlength="255">
      </div>
    </div>
    <div class="field">
      <label class="label">{{ $t('city') }}</label>
      <div class="control">
        <input v-model="city"
               class="input"
               type="text"
               maxlength="255">
      </div>
    </div>
    <div class="field">
      <label class="label">{{ !isUSA ? $t('province'): $t('state') }}</label>
      <div class="control">
        <input v-model="state"
               class="input"
               type="text"
               maxlength="255">
      </div>
    </div>
    <div class="field">
      <label class="label">{{ !isUSA ? $t('postalCode') : $t('zipCode') }}</label>
      <div class="control">
        <input v-model="zipCode"
               class="input"
               type="text"
               maxlength="255">
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Address, AddressInterface } from '@/interfaces/admin/address'
import { ref, computed, onMounted, watch, PropType } from 'vue'
import { useStore } from 'vuex';
import i18n from '@/locales';

const props = defineProps({
  address: {
    type: Object as PropType<AddressInterface | null>,
    required: false,
    default: null
  },
  isRequired: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update']);

const store = useStore();

const countryCode = ref(props.address?.countryCode || '');
const line1 = ref(props.address?.line1 || '');
const line2 = ref(props.address?.line2 || '');
const line3 = ref(props.address?.line3 || '');
const city = ref(props.address?.city || '');
const state = ref(props.address?.state || '');
const zipCode = ref(props.address?.zipCode || '');
const placeId = ref(props.address?.placeId || '');

const countryCodes = computed(() => store.state.countrycodes.items);

const isFormValid = computed(() => {
  if (props.isRequired) {
    switch (countryCode.value) {
      case 'USA':
        return !!(line1.value && city.value && countryCode.value);
      default:
        return !!(line1.value && countryCode.value);
    }
  }
  return true;
});

const isUSA = computed(() => countryCode.value === 'USA');

const getAddressData = () => new Address({
  countryCode: countryCode.value,
  line1: line1.value,
  line2: line2.value,
  line3: line3.value,
  city: city.value,
  state: state.value,
  zipCode: zipCode.value,
  placeId: placeId.value
});

// Method to populate form from address data (e.g., from place details)
const populateFromAddress = (addressData: AddressInterface) => {
  isManualChange = false; // Prevent clearing placeId on programmatic update
  countryCode.value = addressData.countryCode || '';
  line1.value = addressData.line1 || '';
  line2.value = addressData.line2 || '';
  line3.value = addressData.line3 || '';
  city.value = addressData.city || '';
  state.value = addressData.state || '';
  zipCode.value = addressData.zipCode || '';
  placeId.value = addressData.placeId || '';
  // Re-enable manual change detection after next tick
  setTimeout(() => { isManualChange = true; }, 0);
};

// Expose method to parent components
defineExpose({
  populateFromAddress
});

// Track if this is a manual change (user typing) vs programmatic change
let isManualChange = true;

// Watch for changes in any form field and emit an update
// Clear placeId on manual changes to indicate address is no longer validated
watch([countryCode, line1, line2, line3, city, state, zipCode], () => {
  if (isManualChange && placeId.value) {
    placeId.value = ''; // Clear placeId when user manually changes address
  }

  emit('update', {
    address: getAddressData(),
    isFormValid: isFormValid.value
  });
});

store.dispatch('countrycodes/getCountryCodes');

onMounted(() => {
  isManualChange = false; // Prevent clearing placeId on initial mount

  if (props.address) {
    countryCode.value = props.address.countryCode ?? '';
    line1.value = props.address.line1 ?? '';
    line2.value = props.address.line2 ?? '';
    line3.value = props.address.line3 ?? '';
    city.value = props.address.city ?? '';
    state.value = props.address.state ?? '';
    zipCode.value = props.address.zipCode ?? '';
    placeId.value = props.address.placeId ?? '';
  }

  if (!countryCode.value && i18n.global.locale === 'en-US') {
    countryCode.value = 'USA';
  }

  // Re-enable manual change detection after next tick
  setTimeout(() => { isManualChange = true; }, 0);
});

</script>
